import { Box, Heading, Skeleton, useToast, VStack } from "@chakra-ui/react"
import { hasSnipcart } from "@common/Snipcart"
import Banners from "@components/Banners/Banners"
import Breadcrumbs, { CategoryProps } from "@components/Product/Breadcrumbs"
import BuyNowButton from "@components/Product/BuyNowButton"
import ContributorList from "@components/Product/ContributorList"
import DetailedRatings from "@components/Product/DetailedRatings"
import GZBFeatures from "@components/Product/GZBFeatures"
import ProductComingSoon from "@components/Product/ProductComingSoon"
import ProductDescription from "@components/Product/ProductDescription"
import ProductDetails from "@components/Product/ProductDetails"
import ProductFeatures from "@components/Product/ProductFeatures"
import ProductImages from "@components/Product/ProductImages"
import ProductOffers from "@components/Product/ProductOffers"
import ProductShare from "@components/Product/ProductShare"
import QuantitySelector from "@components/Product/QuantitySelector"
import RelatedCategories from "@components/Product/RelatedCategories"
import SpecialProducts from "@components/Product/SpecialProducts"
import StockStatus from "@components/Product/StockStatus"
import Toast from "@components/Product/Toast"
import { product_promos } from "@data/banners"
import { BannerProps } from "@type/banners"
import { OfferData, ProductPageData } from "@type/product"
import { SnipcartProduct } from "@type/snipcart"
import { isDevEnvironment } from "@utils/common"
import { addBusinessDays, convertDateFormat, formatDate } from "@utils/date"
import { offersOffsets } from "@utils/offers"
import { Hit } from "instantsearch.js/es/types/results"
import Head from "next/head"
import { MDXRemoteSerializeResult } from "next-mdx-remote"
import { NextSeo } from "next-seo"
import {
  MouseEventHandler,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react"

type ProductProps = {
  domain: string
  description?: MDXRemoteSerializeResult | null
  data: ProductPageData
  popularProducts: Awaited<{
    hits: Hit[]
    more: string
  }>
  popularProductsLoading: boolean
  og?: string
  noindex: boolean
}

const Product = ({
  domain,
  description,
  data,
  popularProducts,
  popularProductsLoading,
  og,
  noindex,
}: ProductProps) => {
  const isbn13 = data?.isbn ? "ISBN-13" : "EAN"

  const datePublished = data.datePublished
    ? convertDateFormat(data.datePublished, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null

  const attributes = [
    {
      name: "Abridged",
      value: data.abridged || null,
    },
    {
      name: "Audience",
      value: data.audience?.description || null,
    },
    {
      name: "Awards",
      value: data.award || null,
    },
    {
      name: `Author${data.author && data.author.length > 1 ? "s" : ""}`,
      value: data.author || null,
      key: "author.en_AE",
    },
    {
      name: "Book Edition",
      value: data.bookEdition || null,
    },
    {
      name: "Book Format",
      value: data.bookFormat || null,
    },
    {
      name: "Country of Origin",
      value: data.countryOfOrigin || null,
    },
    {
      name: "Date Published",
      value: datePublished,
    },
    {
      name: "Dimensions",
      value:
        data.height && data.width && data.depth
          ? `
      ${data.height?.value && `${data.height.value} x `}
      ${data.width?.value && `${data.width.value} x `}
      ${data.depth?.value && `${data.depth.value} cm`}
    `
          : null,
    },
    {
      name: "Educational Level",
      value:
        (data.educationalLevel &&
          `${data.educationalLevel.inDefinedTermSet}: ${data.educationalLevel.name}`) ||
        null,
    },
    {
      name: "Genre",
      value: data.genre || null,
    },
    {
      name: `Illustrator${
        data.illustrator && data.illustrator.length > 1 ? "s" : ""
      }`,
      value: data.illustrator || null,
      key: "illustrator.en_AE",
    },
    {
      name: "ISBN-10",
      value: data.isbn || null,
    },
    {
      name: isbn13,
      value: data.gtin || null,
    },
    {
      name: "Language",
      value: data.inLanguage || null,
    },
    {
      name: "Location",
      value: data.locationCreated || null,
    },
    {
      name: "Pages",
      value: data.numberOfPages || null,
    },
    {
      name: "Publisher",
      value: data.publisher || null,
      key: "publisher.en_AE",
    },
    {
      name: "Series",
      value:
        data.isPartOf && data.isPartOf.length > 0
          ? data.isPartOf.map((series: { name: string }) => series.name)
          : null,
      key: "isPartOf.en_AE",
    },
    {
      name: "Weight",
      value: data.weight?.value ? `${data.weight.value} gm` : null,
    },
  ]

  const {
    offers: reOffers,
    regularOffset,
    fastOffset,
    price,
  } = useMemo(() => {
    return offersOffsets(data.offers?.offers || [])
  }, [data.offers?.offers])

  const listPrice = data.offers?.priceSpecification?.price || undefined

  const [total, setTotal] = useState<number>(price || 0)

  const offers = [...reOffers].reverse()

  const [selectedOffer, setOffer] = useState<number>(0)

  const [quantity, setQuantity] = useReducer(
    (state: number, action: number) => {
      if (state + action < 1) {
        return 1
      }
      if (state + action > offers?.[selectedOffer]?.inventoryLevel) {
        return offers?.[selectedOffer].inventoryLevel || 1
      }
      return state + action
    },
    1
  )

  const authors =
    data.author?.map((item: string) => {
      return {
        name: item,
        role: "Author",
      }
    }) || []

  const illustrators =
    data.illustrator?.map((item: string) => {
      return {
        name: item,
        role: "Illustrator",
      }
    }) || []

  const outOfStock = !(
    offers &&
    offers.length > 0 &&
    offers[0].availability !== "OutOfStock"
  )

  const toast = useToast()

  const shippingTypes = useMemo(
    () =>
      offers.map((offer, index) => {
        if (index === 0) {
          return offer.deliveryLeadTime.maxValue <= 3 ? "Turbo" : "Regular"
        } else {
          return "Wait and Save"
        }
      }),
    [offers]
  )

  const shippingDates = useMemo(() => {
    return offers?.map((offer: OfferData, index) => {
      const offset = index === 0 ? fastOffset || 0 : regularOffset || 0
      const deliveryMin = formatDate(
        addBusinessDays({
          numDays: offer.deliveryLeadTime.minValue + offset,
        }),
        { month: "short", day: "numeric" }
      )
      const deliveryMax = formatDate(
        addBusinessDays({
          numDays: offer.deliveryLeadTime.maxValue + offset,
        }),
        { month: "short", day: "numeric" }
      )

      return `${deliveryMin} - ${deliveryMax}`
    })
  }, [offers, regularOffset, fastOffset])

  // returns snipcart format - Regular/Turbo|Wait and Save[-10.00]
  const shippingOptions = useMemo(() => {
    const options = offers?.map((offer: OfferData, index: number) => {
      return `${shippingTypes[index]}${
        index > 0 ? `[${(offer.price * 1000 - price * 1000) / 1000}]` : ""
      }`
    })
    return options.join("|") || ""
  }, [offers, price, shippingTypes])

  useEffect(() => {
    // Update quantity based on inventory level
    setQuantity(0)
    // Update total based on selected offer
    if (offers && offers.length > 0) {
      setTotal(quantity * offers[selectedOffer].price)
    }
  }, [offers, quantity, selectedOffer])

  // Add to cart button state
  const [loading, setLoading] = useState<boolean>(false)

  const handleCart: MouseEventHandler<HTMLButtonElement> = async () => {
    setLoading(true)
    const selectedShipping = shippingTypes[selectedOffer]
    const metadata: {
      [key: string]: string
    } = {}

    shippingTypes.map((type, index) => {
      metadata[`${type}`] =
        `${shippingDates[index]} - seller: ${offers[index].seller}`
    })

    // Add book format to metadata
    if (data.bookFormat) {
      metadata["Format"] = data.bookFormat
    }

    if (!isDevEnvironment()) {
      await fetch("/api/snipcart/fetch", {
        method: "POST",
        body: JSON.stringify({ offers: offers, gtin: data.gtin }),
        headers: {
          "Content-Type": "application/json",
        },
      }).catch((error) => {
        console.error(error)
      })
    }

    try {
      const product: SnipcartProduct = {
        id: data.gtin,
        name: data.name,
        price: price,
        url: `${domain}/api/snipcart/product/${data.gtin}`,
        quantity: quantity,
        maxQuantity: offers[selectedOffer].inventoryLevel,
        hasTaxesIncluded: true,
        image:
          data.image && data.image.length > 0
            ? `${process.env.NEXT_PUBLIC_IMAGE_CDN}${data.image[0].contentUrl}?class=mthumb`
            : `${process.env.NEXT_PUBLIC_IMAGE_CDN}/b-img/${data.gtin}.png?class=mthumb`,
        description: `EAN: ${data.gtin}`,
        categories:
          data.category
            ?.filter((cat) => cat.termCode === "BISAC")
            .map((item) => item.name.replace(" | ", " > ")) || [],
        shippable: true,
        taxable: true,
        taxes: ["VAT"],
        dimensions: {
          weight: data.weight && Math.round(data.weight.value), // in grams
          height: data.height && Math.round(data.height.value), // in cm
          width: data.width && Math.round(data.width.value), // in cm
          length: data.depth && Math.round(data.depth.value), // in cm
        },
        customFields: [
          {
            name: "Shipping",
            options: shippingOptions,
            type: "dropdown",
            required: true,
            value: selectedShipping,
          },
        ],
        metadata: metadata,
        stackable: "always",
      }

      hasSnipcart() &&
        (await window.Snipcart?.api.cart.items
          .add(product)
          .then(() => {
            return true
          })
          .then(() => {
            toast({
              status: "success",
              render: ({ id, onClose }) => (
                <Toast
                  id={id}
                  status={"success"}
                  title={"Added to cart"}
                  isClosable
                  onClose={onClose}
                />
              ),
            })
          }))
    } catch (error: unknown) {
      console.error(error)
      toast({
        status: "error",
        render: ({ id, onClose }) => (
          <Toast
            id={id}
            status={"error"}
            title={"Error adding to cart"}
            isClosable
            onClose={onClose}
          />
        ),
      })
    } finally {
      setLoading(false)
    }
  }

  const comingSoon = data.datePublished
    ? new Date(data.datePublished) > new Date()
    : false

  const ogImage =
    data.image && data.image.length > 0
      ? `${process.env.NEXT_PUBLIC_IMAGE_CDN}${data.image[0].contentUrl}`
      : `${process.env.NEXT_PUBLIC_IMAGE_CDN}/b-img/${data.gtin}.png`

  const seoTitleArray = [
    data.name,
    data.author && data.author.length > 0 ? `${data.author.join(", ")}` : "",
    data.gtin,
    data.category && data.category.length > 0
      ? `${data.category[0].name.split(" | ")[0]} Books`
      : "",
  ].filter((item) => item.length > 0)

  return (
    <>
      <Head>
        <script
          type={"application/ld+json"}
          id={"product-jsonld"}
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
          key={`jsonld-product`}
        />
      </Head>

      <NextSeo
        noindex={noindex}
        title={`${seoTitleArray.join(" | ")}`}
        canonical={`${domain}/p/${data.alternateName}/${data.gtin}`}
        description={data.description}
        openGraph={{
          title: data.name,
          description: data.description,
          url: `${domain}/p/${data.alternateName}/${data.gtin}`,
          type: "book",
          book: {
            authors: data.author,
            isbn: data.gtin,
            releaseDate: data.datePublished,
          },
          images: [
            og && og === "telegram"
              ? {
                  url: `${process.env.NEXT_PUBLIC_IMAGE_CDN}/api/og/square.png?image=${ogImage}&class=jpg`,
                  width: 600,
                  height: 600,
                  alt: data.name,
                }
              : {
                  url: `${process.env.NEXT_PUBLIC_IMAGE_CDN}/api/og/rectangle.png?image=${ogImage}&class=jpg`,
                  width: 1200,
                  height: 630,
                  alt: data.name,
                },
          ],
        }}
      />

      <VStack
        spacing={2}
        alignItems={"flex-start"}
      >
        {data.category && (
          <Breadcrumbs
            showFirst={true}
            category={data.category}
          />
        )}

        <Box w={"full"}>
          <ProductShare
            domain={domain}
            gtin={data.gtin}
            name={data.name}
            alternateName={data.alternateName}
          />
        </Box>

        {data.author && (
          <ContributorList items={[...authors, ...illustrators]} />
        )}

        <Box w={"full"}>
          <Heading
            as={"h1"}
            lineHeight={0.8}
          >
            <Box
              as={"span"}
              fontSize={"xl"}
              fontWeight={"500"}
            >
              {data.name}
            </Box>
            {data.bookFormat && (
              <Box
                as={"span"}
                fontSize={"sm"}
                color={"gray.600"}
                fontWeight={"400"}
                display={"block"}
                my={2}
              >
                {data.bookFormat}
                {data.bookEdition && ` - ${data.bookEdition}`}
                {datePublished && `, ${datePublished}`}
              </Box>
            )}
          </Heading>

          {data.aggregateRating &&
            data.aggregateRating.ratingValue > 0 &&
            data.aggregateRating.ratingCount > 0 && (
              <DetailedRatings aggregateRating={data.aggregateRating} />
            )}
        </Box>

        <ProductImages
          data={
            data.image && data.image?.length > 0
              ? data
              : {
                  ...data,
                  image: [
                    {
                      placeholder: true,
                      contentUrl: `${process.env.NEXT_PUBLIC_IMAGE_CDN}/b-img/${data.gtin}.png`,
                      width: { value: 800 },
                      height: { value: 1200 },
                      exifData: [{ value: "JCoGDQDPmHd4enfoWIyGeMNwaQ2H" }],
                    },
                  ],
                }
          }
        />

        {!outOfStock && !comingSoon ? (
          <>
            <QuantitySelector
              quantity={quantity}
              maxQuantity={offers[selectedOffer].inventoryLevel}
              setQuantity={setQuantity}
            />

            <ProductOffers
              offers={offers}
              selectedOffer={selectedOffer}
              setOffer={setOffer}
              regularOffset={regularOffset}
              fastOffset={fastOffset}
              listPrice={listPrice}
            />

            <Box
              pt={4}
              width={"100%"}
            >
              <BuyNowButton
                total={total}
                handleCart={handleCart}
                isLoading={loading}
                loadingText={"Adding to Cart"}
              />
              <StockStatus offer={offers[selectedOffer]} />
            </Box>
          </>
        ) : (
          <ProductComingSoon
            datePublished={datePublished}
            comingSoon={comingSoon}
          />
        )}
        <VStack
          alignItems={"flex-start"}
          spacing={4}
          width={"100%"}
        >
          <GZBFeatures />

          <Banners
            promosLoading={false}
            banners={
              offers[0]?.deliveryLeadTime?.maxValue <= 3
                ? product_promos
                : product_promos?.filter(
                    (b: BannerProps) => b.title !== "Turbo Shipping"
                  )
            }
          />

          <ProductFeatures data={data} />

          <Skeleton
            isLoaded={!popularProductsLoading}
            w={"full"}
            minH={"386px"}
          >
            {!popularProductsLoading && popularProducts?.hits?.length > 0 && (
              <SpecialProducts
                inProductPage
                title={"Popular Books"}
                results={{
                  hits: popularProducts.hits,
                  more: popularProducts.more,
                }}
                style={"slider"}
                shipsTargetHour={15}
              />
            )}
          </Skeleton>

          {(data.disambiguatingDescription || data.description) && (
            <ProductDescription
              mdxDescription={description}
              simpleDescription={data.description}
            />
          )}

          <ProductDetails attributes={attributes} />

          {data.category &&
            data.category.filter(
              (cat: CategoryProps) => cat.termCode === "BISAC"
            ).length > 1 && <RelatedCategories category={data.category} />}
        </VStack>
      </VStack>
    </>
  )
}

export default Product
